import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { Search, Plus, AlertTriangle } from 'lucide-react';

const inr = v => `₹${Number(v||0).toLocaleString('en-IN', {minimumFractionDigits:2})}`;

export default function ItemMasterPage() {
  const [items, setItems]             = useState([]);
  const [categories, setCategories]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [filterCat, setFilterCat]     = useState('');
  const [filterLow, setFilterLow]     = useState(false);
  const [showModal, setShowModal]     = useState(false);
  const [editItem, setEditItem]       = useState(null);
  const [saving, setSaving]           = useState(false);
  const [msg, setMsg]                 = useState('');
  const [form, setForm] = useState({
    name:'', category:'', unit:'NOS', current_stock:'0',
    minimum_stock:'5', notes:''
  });

  const UNITS = ['NOS','KG','LITRE','METER','FEET','TON','BAG','BOX','PACKET','ROLL','SET','PAIR','PIECE','OTHER'];

  const load = useCallback(() => {
    setLoading(true);
    let url = `/purchase/items/?page_size=500`;
    if(search)    url += `&search=${search}`;
    if(filterCat) url += `&category=${filterCat}`;
    if(filterLow) url += `&low_stock=true`;
    api.get(url).then(r => setItems(r.data.results||[])).finally(() => setLoading(false));
  }, [search, filterCat, filterLow]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/purchase/categories/').then(r => setCategories(r.data.results||r.data||[]));
  }, []);

  const openAdd = () => {
    setForm({ name:'', category:'', unit:'NOS', current_stock:'0', minimum_stock:'5', notes:'' });
    setEditItem(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setForm({
      name: item.name,
      category: item.category,
      unit: item.unit,
      current_stock: item.current_stock,
      minimum_stock: item.minimum_stock,
      notes: item.notes||'',
    });
    setEditItem(item);
    setShowModal(true);
  };

  const handleSave = async e => {
    e.preventDefault(); setSaving(true);
    try {
      if(editItem) {
        await api.patch(`/purchase/items/${editItem.id}/`, form);
        setMsg('Item updated!');
      } else {
        await api.post('/purchase/items/', form);
        setMsg('Item added!');
      }
      setShowModal(false); load();
      setTimeout(() => setMsg(''), 3000);
    } catch(err) {
      alert(err.response?.data ? JSON.stringify(err.response.data) : 'Failed to save.');
    }
    finally { setSaving(false); }
  };

  const lowStockCount = items.filter(i => parseFloat(i.current_stock||0) <= parseFloat(i.minimum_stock||0)).length;

  return (
    <Layout title="Item Master" subtitle="Inventory stock levels and item management"
      actions={<button className="btn btn-primary" onClick={openAdd}><Plus size={14}/>Add Item</button>}
    >
      {msg && <div className="success-box">{msg}</div>}

      {/* KPIs */}
      <div className="kpi-grid" style={{gridTemplateColumns:'repeat(4,1fr)',marginBottom:16}}>
        <div className="kpi-card brand"><div className="kpi-value">{items.length}</div><div className="kpi-label">Total Items</div></div>
        <div className="kpi-card green"><div className="kpi-value">{categories.length}</div><div className="kpi-label">Categories</div></div>
        <div className="kpi-card red"><div className="kpi-value">{lowStockCount}</div><div className="kpi-label">Low Stock Items</div></div>
        <div className="kpi-card amber"><div className="kpi-value">{items.filter(i=>parseFloat(i.current_stock||0)>0).length}</div><div className="kpi-label">In Stock</div></div>
      </div>

      {/* Low stock alert */}
      {lowStockCount > 0 && (
        <div style={{padding:'10px 16px',background:'#FDE8E8',borderRadius:'var(--radius-sm)',marginBottom:16,display:'flex',alignItems:'center',gap:10,fontSize:13,color:'var(--red)',fontWeight:600}}>
          <AlertTriangle size={16}/>
          {lowStockCount} item{lowStockCount>1?'s are':' is'} running low on stock!
          <button className="btn btn-sm" style={{marginLeft:'auto',background:'var(--red)',color:'#fff',border:'none'}}
            onClick={()=>setFilterLow(true)}>View Low Stock</button>
        </div>
      )}

      <div className="card">
        {/* Filters */}
        <div className="filter-bar" style={{marginBottom:16}}>
          <div className="search-wrap" style={{flex:2}}>
            <Search size={14}/>
            <input className="search-input" placeholder="Search items..." value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <select className="form-input" style={{width:'auto',minWidth:180}} value={filterCat} onChange={e=>setFilterCat(e.target.value)}>
            <option value="">All Categories</option>
            {categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer',whiteSpace:'nowrap'}}>
            <input type="checkbox" checked={filterLow} onChange={e=>setFilterLow(e.target.checked)}/>
            Low Stock Only
          </label>
          {(filterCat||filterLow) && (
            <button className="btn btn-secondary btn-sm" onClick={()=>{setFilterCat('');setFilterLow(false);}}>Clear</button>
          )}
        </div>

        {loading ? <div className="loading"><div className="spinner"/>Loading...</div> :
          items.length===0 ? <div className="empty-state"><p>No items found.</p></div> :
          <div style={{overflowX:'auto'}}>
            <table>
              <thead>
                <tr>
                  <th>Item Name</th>
                  <th>Category</th>
                  <th>Unit</th>
                  <th>Current Stock</th>
                  <th>Min Stock</th>
                  <th>Status</th>
                  <th>Last Price</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item=>{
                  const isLow = parseFloat(item.current_stock||0) <= parseFloat(item.minimum_stock||0);
                  return (
                    <tr key={item.id} style={{background:isLow?'#fff8f8':''}}>
                      <td style={{fontWeight:600,color:'var(--text-primary)'}}>
                        {isLow && <AlertTriangle size={12} style={{color:'var(--red)',marginRight:6}}/>}
                        {item.name}
                      </td>
                      <td style={{fontSize:12,color:'var(--text-muted)'}}>{item.category_name||'—'}</td>
                      <td style={{fontSize:12}}>{item.unit}</td>
                      <td>
                        <span style={{
                          fontFamily:'DM Mono,monospace',fontWeight:700,
                          color:isLow?'var(--red)':'var(--green)'
                        }}>
                          {parseFloat(item.current_stock||0).toFixed(2)}
                        </span>
                      </td>
                      <td style={{fontFamily:'DM Mono,monospace',fontSize:12,color:'var(--text-muted)'}}>{parseFloat(item.minimum_stock||0).toFixed(2)}</td>
                      <td>
                        {isLow
                          ? <span className="badge badge-red">Low Stock</span>
                          : <span className="badge badge-green">In Stock</span>
                        }
                      </td>
                      <td style={{fontFamily:'DM Mono,monospace',fontSize:12}}>{item.last_price?inr(item.last_price):'—'}</td>
                      <td>
                        <button className="btn btn-sm btn-secondary" onClick={()=>openEdit(item)}>Edit</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        }
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3>{editItem ? 'Edit Item' : 'Add New Item'}</h3>
              <button onClick={()=>setShowModal(false)} style={{background:'none',border:'none',cursor:'pointer',fontSize:18}}>×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label className="form-label">Item Name *</label>
                    <input className="form-input" required value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Category *</label>
                    <select className="form-input" required value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                      <option value="">Select Category</option>
                      {categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unit</label>
                    <select className="form-input" value={form.unit} onChange={e=>setForm(f=>({...f,unit:e.target.value}))}>
                      {UNITS.map(u=><option key={u}>{u}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Current Stock</label>
                    <input className="form-input" type="number" step="0.01" value={form.current_stock} onChange={e=>setForm(f=>({...f,current_stock:e.target.value}))}/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Minimum Stock (Alert Level)</label>
                    <input className="form-input" type="number" step="0.01" value={form.minimum_stock} onChange={e=>setForm(f=>({...f,minimum_stock:e.target.value}))}/>
                  </div>
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label className="form-label">Notes</label>
                    <textarea className="form-input" rows={2} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving?'Saving...':'Save Item'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
